import { useState } from "react";
import { createRoot } from "react-dom/client";
import { QRCodeCanvas } from "qrcode.react";
import { Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

async function renderQrDataUrls(urls, logo) {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0";
  document.body.appendChild(host);
  const root = createRoot(host);
  root.render(<>{urls.map((u) => <QRCodeCanvas key={u} value={u} size={480} level="H" includeMargin fgColor="#0b0c0b" imageSettings={{ src: logo, height: 104, width: 104, excavate: true, crossOrigin: "anonymous" }} />)}</>);
  await new Promise((r) => setTimeout(r, 900));
  const out = Array.from(host.querySelectorAll("canvas")).map((c) => c.toDataURL("image/png"));
  root.unmount(); host.remove();
  return out;
}

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export function TablePdfCard({ restaurant, menuUrl, logo }) {
  const [count, setCount] = useState(10);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    const n = Math.min(200, Math.max(1, Number(count) || 1));
    setBusy(true);
    try {
      const urls = Array.from({ length: n }, (_, i) => `${menuUrl}?tavolo=${i + 1}`);
      const imgs = await renderQrDataUrls(urls, logo);
      const cards = imgs.map((src, i) => `
        <div class="card"><img src="${src}" alt="QR tavolo ${i + 1}" />
          <div class="name">${esc(restaurant.name)}</div>
          <div class="table">Tavolo ${i + 1}</div>
          <div class="hint">Scansiona per vedere il menù</div></div>`).join("");
      const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>QR tavoli · ${esc(restaurant.name)}</title>
        <style>
          @page { size: A4; margin: 12mm; }
          body { font-family: Arial, Helvetica, sans-serif; margin: 0; color: #0b0c0b; }
          .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10mm; }
          .card { border: 1px dashed #bbb; border-radius: 12px; padding: 8mm 6mm; text-align: center; break-inside: avoid; page-break-inside: avoid; }
          .card img { width: 58mm; height: 58mm; }
          .name { font-weight: 700; font-size: 15pt; margin-top: 3mm; }
          .table { font-weight: 800; font-size: 22pt; color: #4a8a56; margin-top: 1mm; }
          .hint { color: #777; font-size: 9pt; margin-top: 2mm; }
        </style></head><body><div class="grid">${cards}</div>
        <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
      const w = window.open("", "_blank");
      if (!w) { toast.error("Consenti le finestre pop-up per generare il PDF"); return; }
      w.document.open(); w.document.write(html); w.document.close();
      toast.success(`PDF con ${n} tavoli pronto: scegli "Salva come PDF" nella finestra di stampa.`);
    } catch (e) {
      toast.error("Impossibile generare il PDF, riprova.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl mo-card p-6" data-testid="table-pdf-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-[#5aa469]/12 flex items-center justify-center"><Printer className="h-5 w-5 text-[#4a8a56]" /></div>
        <div>
          <h2 className="font-display font-semibold text-xl">Stampa QR per i tavoli</h2>
          <p className="text-sm text-gray-500">Un PDF pronto da stampare: QR, nome del locale e numero del tavolo su ogni cartoncino.</p>
        </div>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label>Numero di tavoli</Label>
          <Input type="number" min="1" max="200" value={count} onChange={(e) => setCount(e.target.value)} className="mt-1.5 h-11 w-32 rounded-lg" data-testid="table-pdf-count" />
        </div>
        <Button onClick={generate} disabled={busy} className="h-11 rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="table-pdf-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Printer className="h-4 w-4 mr-2" />Genera PDF tavoli</>}
        </Button>
      </div>
    </div>
  );
}
